import { getDocumentContent, type GoldenClient } from "@platform/api/golden";

/** A document the reviewer can open: one of the case's, or the one an evidence entry names. */
export interface DocumentRef {
  readonly id: number;
  readonly fileName: string;
}

const PDF = /\.pdf$/i;

const fetchDocument = async (
  client: GoldenClient,
  caseId: number,
  documentId: number,
): Promise<Blob> => {
  const { data } = await getDocumentContent({
    client,
    path: { case_id: caseId, document_id: documentId },
    throwOnError: true,
  });
  return data;
};

/** Saves a case document under its own file name. */
export const downloadCaseDocument = async (
  client: GoldenClient,
  caseId: number,
  document: DocumentRef,
): Promise<void> => {
  const url = URL.createObjectURL(
    await fetchDocument(client, caseId, document.id),
  );
  const link = window.document.createElement("a");
  link.href = url;
  link.download = document.fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Opens a PDF in a new tab at a page, through the browser's own viewer, which reads
 * #page=N. The tab opens before anything is awaited: a browser allows a new window
 * only for a short while after the click, and fetching the document, its token
 * included, can outlast that. A file that is not a PDF has no pages, so it downloads.
 */
export const openCaseDocument = async (
  client: GoldenClient,
  caseId: number,
  document: DocumentRef,
  page: number | null,
): Promise<void> => {
  if (!PDF.test(document.fileName)) {
    await downloadCaseDocument(client, caseId, document);
    return;
  }
  const viewer = window.open("", "_blank");
  if (!viewer) throw new Error("The browser blocked the document tab");
  viewer.opener = null;
  try {
    const pdf = new Blob([await fetchDocument(client, caseId, document.id)], {
      type: "application/pdf",
    });
    // Not revoked: the viewer tab reads it for as long as it is open.
    const url = URL.createObjectURL(pdf);
    viewer.location.href = page === null ? url : `${url}#page=${page}`;
  } catch (error: unknown) {
    viewer.close();
    throw error;
  }
};
